import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaRegCalendarAlt } from "react-icons/fa";
import { usePortalPosition } from "../../TaskManagement/usePortalPosition";
import {
  FILTER_CHEVRON_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  formatDisplayDate,
  getDropdownOptionStyle,
} from "./commissionsConstants";
import { DUE_FILTER_OPTIONS } from "./commissionInstallmentsQueueUtils";

function resolveDueFilterLabel(dueFilter, startDate, endDate) {
  if (dueFilter === "custom") {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
    }
    if (startDate) {
      return `From ${formatDisplayDate(startDate)}`;
    }
    if (endDate) {
      return `Until ${formatDisplayDate(endDate)}`;
    }
    return "Custom Date";
  }
  return DUE_FILTER_OPTIONS.find((o) => o.id === dueFilter)?.label || "All";
}

function CommissionInstallmentsDueFilterField({
  dueFilter,
  onDueFilterChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open, {
    placement: "bottom",
    gap: 6,
  });

  const displayLabel = resolveDueFilterLabel(
    dueFilter,
    customStartDate,
    customEndDate,
  );
  const isPlaceholder = dueFilter === "all";

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectPreset = (id) => {
    onDueFilterChange(id);
    if (id !== "custom") {
      onCustomStartDateChange("");
      onCustomEndDateChange("");
      setOpen(false);
    }
  };

  const menu = (
    <div
      ref={menuRef}
      className="tailwind-container z-[9999] max-w-[calc(100vw-1rem)] rounded-xl border border-gray-200 bg-white p-2 shadow-lg sm:max-w-[20rem]"
      style={{
        ...portalStyle,
        minWidth: "min(100%, 17.5rem)",
      }}
    >
      <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Due date
      </p>
      <div className="space-y-0.5">
        {DUE_FILTER_OPTIONS.map((option) => {
          const active = dueFilter === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => selectPreset(option.id)}
              className="w-full text-left text-[13px] transition"
              style={{
                ...getDropdownOptionStyle(active),
                borderRadius: "8px",
                padding: "8px 10px",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.backgroundColor = "#F9FAFB";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {dueFilter === "custom" ? (
        <div className="mt-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2.5">
          <p className="mb-2 text-[12px] font-medium text-gray-600">
            Pick a date range
          </p>
          <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2">
            <div className="min-w-0">
              <label
                htmlFor="installments-due-from"
                className="mb-1 block text-[11px] font-medium text-gray-500"
              >
                Start
              </label>
              <input
                id="installments-due-from"
                type="date"
                value={customStartDate}
                max={customEndDate || undefined}
                onChange={(e) => onCustomStartDateChange(e.target.value)}
                className="h-[34px] w-full rounded-lg border border-gray-200 bg-white px-2 text-[13px] text-gray-800 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
              />
            </div>
            <div className="min-w-0">
              <label
                htmlFor="installments-due-to"
                className="mb-1 block text-[11px] font-medium text-gray-500"
              >
                End
              </label>
              <input
                id="installments-due-to"
                type="date"
                value={customEndDate}
                min={customStartDate || undefined}
                onChange={(e) => onCustomEndDateChange(e.target.value)}
                className="h-[34px] w-full rounded-lg border border-gray-200 bg-white px-2 text-[13px] text-gray-800 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
              />
            </div>
          </div>
          <p className="mb-0 mt-2 text-[11px] leading-snug text-gray-500">
            Results update when you choose dates. Leave either field open for an
            open-ended range.
          </p>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="min-w-0 w-full" ref={containerRef}>
      <label className={FILTER_LABEL_CLASS}>Due date</label>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={`${FILTER_TRIGGER_CLASS} ${open ? FILTER_TRIGGER_OPEN_CLASS : ""} cursor-pointer`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">
            <FaRegCalendarAlt size={12} className="shrink-0 text-gray-400" />
            <span
              className={`truncate ${isPlaceholder ? "text-gray-400" : "font-medium text-gray-800"}`}
            >
              {displayLabel}
            </span>
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open ? createPortal(menu, document.body) : null}
      </div>
    </div>
  );
}

export default CommissionInstallmentsDueFilterField;
